import { typeSafeObjectEntries } from '../util/typeSafeObjectEntries';
import { typeSafeObjectKeys } from '../util/typeSafeObjectKeys';

type ColorComponents = {
  r: number;
  g: number;
  b: number;
  a?: number;
};
type ColorDefinition = {
  components: ColorComponents;
  base: string;
  light: string;
  lightest: string;
  hover: string;
  pressed: string;
};

export enum NeutralPaletteColorName {
  DayFg1 = 'DayFg1',
  DayFg2 = 'DayFg2',
  DayFg3 = 'DayFg3',
  DayBg1 = 'DayBg1',
  DayBg2 = 'DayBg2',
  DayBg3 = 'DayBg3',
  NightFg1 = 'NightFg1',
  NightFg2 = 'NightFg2',
  NightFg3 = 'NightFg3',
  NightBg1 = 'NightBg1',
  NightBg2 = 'NightBg2',
  NightBg3 = 'NightBg3',
  CryptoFg1 = 'CryptoFg1',
  CryptoFg2 = 'CryptoFg2',
  CryptoFg3 = 'CryptoFg3',
  CryptoBg1 = 'CryptoBg1',
  CryptoBg2 = 'CryptoBg2',
  CryptoBg3 = 'CryptoBg3',
  ElevatedNightFg1 = 'ElevatedNightFg1',
  ElevatedNightFg2 = 'ElevatedNightFg2',
  ElevatedNightFg3 = 'ElevatedNightFg3',
  ElevatedNightBg1 = 'ElevatedNightBg1',
  ElevatedNightBg2 = 'ElevatedNightBg2',
  ElevatedNightBg3 = 'ElevatedNightBg3',
  ScaryBg1 = 'ScaryBg1',
  ScaryBg3 = 'ScaryBg3',
  ScaryAccessibleColorsBg1 = 'ScaryAccessibleColorsBg1',
  ScaryAccessibleColorsBg3 = 'ScaryAccessibleColorsBg3',
  CreditGoldFg1 = 'CreditGoldFg1',
  CreditGoldFg2 = 'CreditGoldFg2',
  CreditGoldFg3 = 'CreditGoldFg3',
  CreditGoldBg1 = 'CreditGoldBg1',
  CreditGoldBg2 = 'CreditGoldBg2',
  CreditGoldBg3 = 'CreditGoldBg3',
  BankingFg1 = 'BankingFg1',
  BankingFg2 = 'BankingFg2',
  BankingFg3 = 'BankingFg3',
  BankingBg1 = 'BankingBg1',
  BankingBg2 = 'BankingBg2',
  BankingBg3 = 'BankingBg3',
}

/**
 * Build out an object of every color in the neutral palette, with each color having both an
 * 'rgb(...)' string and the RGB number values.
 */
type OverlayParams = {
  lightAlpha: number;
  lightestAlpha: number;
  hover: [number, number, number, number];
  pressed: [number, number, number, number];
};
const fgLightOverlays: OverlayParams = {
  hover: [255, 255, 255, 0.15],
  lightAlpha: 0.3,
  lightestAlpha: 0.1,
  pressed: [255, 255, 255, 0.25],
};
const bgLightOverlays: OverlayParams = {
  hover: [0, 0, 0, 0.05],
  lightAlpha: 0.3,
  lightestAlpha: 0.1,
  pressed: [0, 0, 0, 0.1],
};
const fgDarkOverlays: OverlayParams = {
  hover: [0, 0, 0, 0.05],
  lightAlpha: 0.4,
  lightestAlpha: 0.2,
  pressed: [0, 0, 0, 0.1],
};
const bgDarkOverlays: OverlayParams = {
  hover: [255, 255, 255, 0.1],
  lightAlpha: 0.4,
  lightestAlpha: 0.2,
  pressed: [255, 255, 255, 0.2],
};
/* eslint-disable sort-keys-fix/sort-keys-fix */
const NeutralPaletteFGLightComponents = {
  [NeutralPaletteColorName.DayFg1]: { r: 0, g: 0, b: 0 },
  [NeutralPaletteColorName.DayFg2]: { r: 106, g: 114, b: 120 },
  [NeutralPaletteColorName.DayFg3]: { r: 180, g: 189, b: 194 },
  [NeutralPaletteColorName.CreditGoldFg1]: { r: 239, g: 229, b: 208 },
  [NeutralPaletteColorName.CreditGoldFg2]: { r: 180, g: 174, b: 159 },
  [NeutralPaletteColorName.CreditGoldFg3]: { r: 135, g: 127, b: 122 },
  [NeutralPaletteColorName.CreditGoldBg1]: { r: 28, g: 24, b: 13 },
  [NeutralPaletteColorName.CreditGoldBg2]: { r: 47, g: 41, b: 39 },
  [NeutralPaletteColorName.CreditGoldBg3]: { r: 57, g: 55, b: 54 },
  [NeutralPaletteColorName.BankingFg1]: { r: 217, g: 216, b: 211 },
  [NeutralPaletteColorName.BankingFg2]: { r: 171, g: 166, b: 156 },
  [NeutralPaletteColorName.BankingFg3]: { r: 110, g: 105, b: 94 },
  [NeutralPaletteColorName.BankingBg1]: { r: 13, g: 11, b: 8 },
  [NeutralPaletteColorName.BankingBg2]: { r: 28, g: 26, b: 23 },
  [NeutralPaletteColorName.BankingBg3]: { r: 39, g: 37, b: 33 },
} as const;
const NeutralPaletteBGLightComponents = {
  [NeutralPaletteColorName.DayBg1]: { r: 255, g: 255, b: 255 },
  [NeutralPaletteColorName.DayBg2]: { r: 245, g: 248, b: 250 },
  [NeutralPaletteColorName.DayBg3]: { r: 227, g: 233, b: 237 },
  [NeutralPaletteColorName.ScaryBg1]: { r: 255, g: 80, b: 0 },
  [NeutralPaletteColorName.ScaryBg3]: { r: 255, g: 80, b: 0 },
  [NeutralPaletteColorName.ScaryAccessibleColorsBg1]: { r: 218, g: 65, b: 107 },
  [NeutralPaletteColorName.ScaryAccessibleColorsBg3]: { r: 218, g: 65, b: 107 },
} as const;
const NeutralPaletteFGDarkComponents = {
  [NeutralPaletteColorName.NightFg1]: { r: 255, g: 255, b: 255 },
  [NeutralPaletteColorName.NightFg2]: { r: 145, g: 159, b: 166 },
  [NeutralPaletteColorName.NightFg3]: { r: 84, g: 93, b: 97 },
  [NeutralPaletteColorName.CryptoFg1]: { r: 255, g: 255, b: 255 },
  [NeutralPaletteColorName.CryptoFg2]: { r: 155, g: 149, b: 170 },
  [NeutralPaletteColorName.CryptoFg3]: { r: 94, g: 89, b: 107 },
  [NeutralPaletteColorName.ElevatedNightFg1]: { r: 255, g: 255, b: 255 },
  [NeutralPaletteColorName.ElevatedNightFg2]: { r: 156, g: 171, b: 178 },
  [NeutralPaletteColorName.ElevatedNightFg3]: { r: 98, g: 108, b: 112 },
} as const;
const NeutralPaletteBGDarkComponents = {
  [NeutralPaletteColorName.NightBg1]: { r: 0, g: 0, b: 0 },
  [NeutralPaletteColorName.NightBg2]: { r: 30, g: 33, b: 36 },
  [NeutralPaletteColorName.NightBg3]: { r: 48, g: 54, b: 58 },
  [NeutralPaletteColorName.CryptoBg1]: { r: 16, g: 10, b: 32 },
  [NeutralPaletteColorName.CryptoBg2]: { r: 36, g: 30, b: 51 },
  [NeutralPaletteColorName.CryptoBg3]: { r: 56, g: 51, b: 69 },
  [NeutralPaletteColorName.ElevatedNightBg1]: { r: 30, g: 33, b: 36 },
  [NeutralPaletteColorName.ElevatedNightBg2]: { r: 48, g: 54, b: 58 },
  [NeutralPaletteColorName.ElevatedNightBg3]: { r: 64, g: 73, b: 78 },
} as const;
/* eslint-enable sort-keys-fix/sort-keys-fix */
function createColorForComponents(components: { r: number; g: number; b: number }, overlayParams: OverlayParams) {
  const { b, g, r } = components;
  return {
    base: `rgb(${r}, ${g}, ${b})`,
    components,
    hover: `rgba(${overlayColor(r, g, b, 1, ...overlayParams.hover).join(', ')})`,
    light: `rgba(${r}, ${g}, ${b}, ${overlayParams.lightAlpha})`,
    lightest: `rgba(${r}, ${g}, ${b}, ${overlayParams.lightestAlpha})`,
    pressed: `rgba(${overlayColor(r, g, b, 1, ...overlayParams.pressed).join(', ')})`,
  };
}
export const NeutralPaletteColors = {
  // This repetition is pretty unfortunate, but I couldn't figure out a way to type a helper
  // method that would allow the types to pass through and produce the correct result.
  ...typeSafeObjectEntries(NeutralPaletteFGLightComponents).reduce(
    (obj, [name, components]) => ({
      ...obj,
      [name]: createColorForComponents(components, fgLightOverlays),
    }),
    {} as { [K in NeutralPaletteColorName]: ColorDefinition },
  ),
  ...typeSafeObjectEntries(NeutralPaletteBGLightComponents).reduce(
    (obj, [name, components]) => ({
      ...obj,
      [name]: createColorForComponents(components, bgLightOverlays),
    }),
    {} as { [K in NeutralPaletteColorName]: ColorDefinition },
  ),
  ...typeSafeObjectEntries(NeutralPaletteFGDarkComponents).reduce(
    (obj, [name, components]) => ({
      ...obj,
      [name]: createColorForComponents(components, fgDarkOverlays),
    }),
    {} as { [K in NeutralPaletteColorName]: ColorDefinition },
  ),
  ...typeSafeObjectEntries(NeutralPaletteBGDarkComponents).reduce(
    (obj, [name, components]) => ({
      ...obj,
      [name]: createColorForComponents(components, bgDarkOverlays),
    }),
    {} as { [K in NeutralPaletteColorName]: ColorDefinition },
  ),
};

/**
 * Define the extended color palette.
 */
export enum ExtendedPaletteColorName {
  CreditGoldAccent = 'CreditGoldAccent',
  BankingAccentLight = 'BankingAccentLight',
  BankingAccentDark = 'BankingAccentDark',
  BankingError = 'BankingError',
  Jet = 'Jet',
  Nova = 'Nova',
  JouleLight = 'JouleLight',
  Joule = 'Joule',
  JouleDark = 'JouleDark',
  SolLight = 'SolLight',
  Sol = 'Sol',
  SolDark = 'SolDark',
  LumenLight = 'LumenLight',
  Lumen = 'Lumen',
  LumenDark = 'LumenDark',
  Mineral = 'Mineral',
  Andros = 'Andros',
  XRayLight = 'XRayLight',
  XRay = 'XRay',
  XRayDark = 'XRayDark',
  PrimeLight = 'PrimeLight',
  Prime = 'Prime',
  PrimeDark = 'PrimeDark',
  BiomeLight = 'BiomeLight',
  Biome = 'Biome',
  BiomeDark = 'BiomeDark',
  Ion = 'Ion',
  Jade = 'Jade',
  DotLight = 'DotLight',
  Dot = 'Dot',
  DotDark = 'DotDark',
  HydroLight = 'HydroLight',
  Hydro = 'Hydro',
  HydroDark = 'HydroDark',
  CosmonautLight = 'CosmonautLight',
  Cosmonaut = 'Cosmonaut',
  CosmonautDark = 'CosmonautDark',
  Stratos = 'Stratos',
  Exos = 'Exos',
  UVLight = 'UVLight',
  UV = 'UV',
  UVDark = 'UVDark',
  IrisLight = 'IrisLight',
  Iris = 'Iris',
  IrisDark = 'IrisDark',
  MilleniumLight = 'MilleniumLight',
  Millenium = 'Millenium',
  MilleniumDark = 'MilleniumDark',
  Ruby = 'Ruby',
  ResinLight = 'ResinLight',
  Resin = 'Resin',
  ResinDark = 'ResinDark',
  DroidLight = 'DroidLight',
  Droid = 'Droid',
  DroidDark = 'DroidDark',
  CloneLight = 'CloneLight',
  Clone = 'Clone',
  CloneDark = 'CloneDark',
  Gaia = 'Gaia',
  SocialTwitter = 'SocialTwitter',
  LegacyStratosDark = 'LegacyStratosDark',
  LegacyGreen = 'LegacyGreen',
  LegacyRed = 'LegacyRed',
  Bloom = 'Bloom',
  Flare = 'Flare',
  Morph = 'Morph',
  Moon = 'Moon',
  Astro = 'Astro',
  Neptune = 'Neptune',
  Synth = 'Synth',
}

/* eslint-disable sort-keys-fix/sort-keys-fix */
const LightThemeExtendedPaletteComponents = {
  [ExtendedPaletteColorName.Jet]: { r: 0, g: 0, b: 0 },
  [ExtendedPaletteColorName.Nova]: { r: 255, g: 255, b: 255 },
  [ExtendedPaletteColorName.JouleLight]: { r: 254, g: 121, b: 76 },
  [ExtendedPaletteColorName.Joule]: { r: 255, g: 80, b: 0 },
  [ExtendedPaletteColorName.JouleDark]: { r: 202, g: 61, b: 0 },
  [ExtendedPaletteColorName.SolLight]: { r: 255, g: 198, b: 87 },
  [ExtendedPaletteColorName.Sol]: { r: 252, g: 160, b: 0 },
  [ExtendedPaletteColorName.SolDark]: { r: 245, g: 139, b: 0 },
  [ExtendedPaletteColorName.LumenLight]: { r: 255, g: 232, b: 108 },
  [ExtendedPaletteColorName.Lumen]: { r: 255, g: 221, b: 0 },
  [ExtendedPaletteColorName.LumenDark]: { r: 252, g: 201, b: 33 },
  [ExtendedPaletteColorName.Mineral]: { r: 240, g: 235, b: 230 },
  [ExtendedPaletteColorName.Andros]: { r: 84, g: 26, b: 0 },
  [ExtendedPaletteColorName.XRayLight]: { r: 195, g: 245, b: 60 },
  [ExtendedPaletteColorName.XRay]: { r: 125, g: 202, b: 0 },
  [ExtendedPaletteColorName.XRayDark]: { r: 110, g: 177, b: 0 },
  [ExtendedPaletteColorName.PrimeLight]: { r: 126, g: 232, b: 99 },
  [ExtendedPaletteColorName.Prime]: { r: 0, g: 200, b: 5 },
  [ExtendedPaletteColorName.PrimeDark]: { r: 0, g: 155, b: 4 },
  [ExtendedPaletteColorName.BiomeLight]: { r: 60, g: 148, b: 106 },
  [ExtendedPaletteColorName.Biome]: { r: 0, g: 95, b: 50 },
  [ExtendedPaletteColorName.BiomeDark]: { r: 0, g: 64, b: 34 },
  [ExtendedPaletteColorName.CreditGoldAccent]: { r: 255, g: 214, b: 112 },
  [ExtendedPaletteColorName.BankingAccentLight]: { r: 207, g: 185, b: 125 },
  [ExtendedPaletteColorName.BankingAccentDark]: { r: 148, g: 129, b: 76 },
  [ExtendedPaletteColorName.BankingError]: { r: 255, g: 76, b: 76 },
  [ExtendedPaletteColorName.Ion]: { r: 225, g: 235, b: 220 },
  [ExtendedPaletteColorName.Jade]: { r: 0, g: 38, b: 21 },
  [ExtendedPaletteColorName.DotLight]: { r: 130, g: 200, b: 210 },
  [ExtendedPaletteColorName.Dot]: { r: 104, g: 179, b: 190 },
  [ExtendedPaletteColorName.DotDark]: { r: 50, g: 138, b: 151 },
  [ExtendedPaletteColorName.HydroLight]: { r: 137, g: 198, b: 255 },
  [ExtendedPaletteColorName.Hydro]: { r: 0, g: 127, b: 245 },
  [ExtendedPaletteColorName.HydroDark]: { r: 0, g: 81, b: 193 },
  [ExtendedPaletteColorName.CosmonautLight]: { r: 153, g: 122, b: 236 },
  [ExtendedPaletteColorName.Cosmonaut]: { r: 80, g: 50, b: 160 },
  [ExtendedPaletteColorName.CosmonautDark]: { r: 56, g: 36, b: 109 },
  [ExtendedPaletteColorName.Stratos]: { r: 215, g: 230, b: 255 },
  [ExtendedPaletteColorName.Exos]: { r: 0, g: 42, b: 81 },
  [ExtendedPaletteColorName.UVLight]: { r: 255, g: 116, b: 154 },
  [ExtendedPaletteColorName.UV]: { r: 218, g: 65, b: 107 },
  [ExtendedPaletteColorName.UVDark]: { r: 184, g: 57, b: 93 },
  [ExtendedPaletteColorName.IrisLight]: { r: 208, g: 147, b: 208 },
  [ExtendedPaletteColorName.Iris]: { r: 160, g: 96, b: 160 },
  [ExtendedPaletteColorName.IrisDark]: { r: 134, g: 65, b: 134 },
  [ExtendedPaletteColorName.MilleniumLight]: { r: 240, g: 170, b: 165 },
  [ExtendedPaletteColorName.Millenium]: { r: 223, g: 136, b: 129 },
  [ExtendedPaletteColorName.MilleniumDark]: { r: 180, g: 99, b: 93 },
  [ExtendedPaletteColorName.Ruby]: { r: 82, g: 20, b: 43 },
  [ExtendedPaletteColorName.ResinLight]: { r: 235, g: 143, b: 43 },
  [ExtendedPaletteColorName.Resin]: { r: 210, g: 110, b: 0 },
  [ExtendedPaletteColorName.ResinDark]: { r: 184, g: 88, b: 0 },
  [ExtendedPaletteColorName.DroidLight]: { r: 205, g: 180, b: 65 },
  [ExtendedPaletteColorName.Droid]: { r: 179, g: 145, b: 11 },
  [ExtendedPaletteColorName.DroidDark]: { r: 157, g: 128, b: 10 },
  [ExtendedPaletteColorName.CloneLight]: { r: 206, g: 191, b: 144 },
  [ExtendedPaletteColorName.Clone]: { r: 186, g: 168, b: 114 },
  [ExtendedPaletteColorName.CloneDark]: { r: 147, g: 130, b: 82 },
  [ExtendedPaletteColorName.Gaia]: { r: 46, g: 29, b: 15 },

  [ExtendedPaletteColorName.SocialTwitter]: { r: 29, g: 161, b: 242 },

  [ExtendedPaletteColorName.LegacyStratosDark]: { r: 228, g: 238, b: 255 },
  [ExtendedPaletteColorName.LegacyGreen]: { r: 33, g: 206, b: 153 },
  [ExtendedPaletteColorName.LegacyRed]: { r: 244, g: 85, b: 49 },

  [ExtendedPaletteColorName.Moon]: { r: 49, g: 146, b: 249 },
  [ExtendedPaletteColorName.Morph]: { r: 110, g: 177, b: 0 },
  [ExtendedPaletteColorName.Bloom]: { r: 218, g: 65, b: 107 },
  [ExtendedPaletteColorName.Flare]: { r: 239, g: 150, b: 61 },
  [ExtendedPaletteColorName.Astro]: { r: 47, g: 19, b: 145 },
  [ExtendedPaletteColorName.Neptune]: { r: 0, g: 81, b: 193 },
  [ExtendedPaletteColorName.Synth]: { r: 0, g: 127, b: 245 },
} as const;
/* eslint-enable sort-keys-fix/sort-keys-fix */

/* eslint-disable sort-keys-fix/sort-keys-fix */
const DarkThemeExtendedPaletteComponents = {
  ...LightThemeExtendedPaletteComponents,
  // Some colors have different values in dark themes.
  [ExtendedPaletteColorName.Sol]: { r: 252, g: 170, b: 5 },
  [ExtendedPaletteColorName.SolDark]: { r: 245, g: 135, b: 0 },
  [ExtendedPaletteColorName.LumenLight]: { r: 255, g: 241, b: 109 },
  [ExtendedPaletteColorName.Lumen]: { r: 255, g: 230, b: 0 },
  [ExtendedPaletteColorName.LumenDark]: { r: 242, g: 204, b: 1 },
  [ExtendedPaletteColorName.XRayLight]: { r: 216, g: 255, b: 109 },
  [ExtendedPaletteColorName.XRay]: { r: 195, g: 245, b: 60 },
  [ExtendedPaletteColorName.XRayDark]: { r: 125, g: 202, b: 0 },
  [ExtendedPaletteColorName.BiomeLight]: { r: 95, g: 187, b: 144 },
  [ExtendedPaletteColorName.Biome]: { r: 35, g: 135, b: 88 },
  [ExtendedPaletteColorName.BiomeDark]: { r: 1, g: 82, b: 44 },
  [ExtendedPaletteColorName.DotLight]: { r: 168, g: 228, b: 236 },
  [ExtendedPaletteColorName.Dot]: { r: 130, g: 200, b: 210 },
  [ExtendedPaletteColorName.DotDark]: { r: 81, g: 159, b: 170 },
  [ExtendedPaletteColorName.CosmonautLight]: { r: 176, g: 149, b: 249 },
  [ExtendedPaletteColorName.Cosmonaut]: { r: 135, g: 97, b: 236 },
  [ExtendedPaletteColorName.CosmonautDark]: { r: 76, g: 45, b: 158 },
  [ExtendedPaletteColorName.UVLight]: { r: 255, g: 144, b: 175 },
  [ExtendedPaletteColorName.UV]: { r: 255, g: 90, b: 135 },
  [ExtendedPaletteColorName.UVDark]: { r: 218, g: 65, b: 107 },
  [ExtendedPaletteColorName.IrisLight]: { r: 226, g: 166, b: 226 },
  [ExtendedPaletteColorName.Iris]: { r: 190, g: 130, b: 190 },
  [ExtendedPaletteColorName.IrisDark]: { r: 151, g: 84, b: 151 },
  [ExtendedPaletteColorName.MilleniumLight]: { r: 249, g: 195, b: 192 },
  [ExtendedPaletteColorName.Millenium]: { r: 240, g: 170, b: 165 },
  [ExtendedPaletteColorName.MilleniumDark]: { r: 219, g: 135, b: 128 },
  [ExtendedPaletteColorName.DroidLight]: { r: 226, g: 204, b: 104 },
  [ExtendedPaletteColorName.Droid]: { r: 205, g: 180, b: 65 },
  [ExtendedPaletteColorName.DroidDark]: { r: 192, g: 154, b: 0 },
  [ExtendedPaletteColorName.CloneLight]: { r: 234, g: 219, b: 172 },
  [ExtendedPaletteColorName.Clone]: { r: 220, g: 200, b: 140 },
  [ExtendedPaletteColorName.CloneDark]: { r: 201, g: 177, b: 106 },
  [ExtendedPaletteColorName.Moon]: { r: 60, g: 135, b: 251 },
  [ExtendedPaletteColorName.Morph]: { r: 195, g: 245, b: 60 },
  [ExtendedPaletteColorName.Bloom]: { r: 255, g: 136, b: 161 },
  [ExtendedPaletteColorName.Flare]: { r: 253, g: 233, b: 48 },
} as const;
/* eslint-enable sort-keys-fix/sort-keys-fix */

export const ExtendedPaletteColorNames = typeSafeObjectKeys(LightThemeExtendedPaletteComponents);

/**
 * Returns a boolean indicating whether or not the specified string is the name of a color in
 * the extended palette. Also performs type narrowing.
 */
export function getIsExtendedPaletteColorName(name: string): name is ExtendedPaletteColorName {
  return name in LightThemeExtendedPaletteComponents;
}

type ExtendedPaletteOverlays = {
  [k in ExtendedPaletteColorName]?: {
    hover: [number, number, number, number];
    pressed: [number, number, number, number];
  };
};
const lightThemeCustomOverlays: ExtendedPaletteOverlays = {
  [ExtendedPaletteColorName.Jet]: {
    hover: [255, 255, 255, 0.15],
    pressed: [255, 255, 255, 0.25],
  },
  [ExtendedPaletteColorName.Nova]: {
    hover: [0, 0, 0, 0.05],
    pressed: [0, 0, 0, 0.1],
  },
};
const darkThemeCustomOverlays: ExtendedPaletteOverlays = {
  [ExtendedPaletteColorName.Jet]: {
    hover: [255, 255, 255, 0.1],
    pressed: [255, 255, 255, 0.2],
  },
  [ExtendedPaletteColorName.Nova]: {
    hover: [0, 0, 0, 0.05],
    pressed: [0, 0, 0, 0.1],
  },
};
const LightThemeExtendedPaletteColors = typeSafeObjectEntries(LightThemeExtendedPaletteComponents).reduce(
  (obj, [name, components]) => {
    const { b, g, r } = components;
    const overlays = lightThemeCustomOverlays[name] || {
      hover: [0, 0, 0, 0.1],
      pressed: [0, 0, 0, 0.2],
    };

    return {
      ...obj,
      [name]: {
        base: `rgb(${r}, ${g}, ${b})`,
        components,
        hover: `rgba(${overlayColor(r, g, b, 1, ...overlays.hover).join(', ')})`,
        light: `rgba(${r}, ${g}, ${b}, 0.3)`,
        lightest: `rgba(${r}, ${g}, ${b}, 0.1)`,
        pressed: `rgba(${overlayColor(r, g, b, 1, ...overlays.pressed).join(', ')})`,
      },
    };
  },
  {} as { [K in ExtendedPaletteColorName]: ColorDefinition },
);
const DarkThemeExtendedPaletteColors = typeSafeObjectEntries(DarkThemeExtendedPaletteComponents).reduce(
  (obj, [name, components]) => {
    const { b, g, r } = components;
    const overlays = darkThemeCustomOverlays[name] || {
      hover: [255, 255, 255, 0.15],
      pressed: [255, 255, 255, 0.25],
    };

    return {
      ...obj,
      [name]: {
        base: `rgb(${r}, ${g}, ${b})`,
        components,
        hover: `rgba(${overlayColor(r, g, b, 1, ...overlays.hover).join(', ')})`,
        light: `rgba(${r}, ${g}, ${b}, 0.4)`,
        lightest: `rgba(${r}, ${g}, ${b}, 0.2)`,
        pressed: `rgba(${overlayColor(r, g, b, 1, ...overlays.pressed).join(', ')})`,
      },
    };
  },
  {} as { [K in ExtendedPaletteColorName]: ColorDefinition },
);

/**
 * Returns the color definition for the specified color from the extended palette in a
 * light theme.
 */
export function getLightThemeExtendedColor(name: ExtendedPaletteColorName): ColorDefinition {
  return LightThemeExtendedPaletteColors[name];
}

/**
 * Returns the color definition for the specified color from the extended palette in a
 * dark theme.
 */
export function getDarkThemeExtendedColor(name: ExtendedPaletteColorName): ColorDefinition {
  return DarkThemeExtendedPaletteColors[name];
}

/**
 * Returns an object with the RGBA values for the specified color from the extended palette
 * in a light theme.
 */
export function getLightThemeExtendedColorComponents(name: ExtendedPaletteColorName): ColorComponents {
  return getLightThemeExtendedColor(name).components;
}

/**
 * Returns an object with the RGBA values for the specified color from the extended palette
 * in a dark theme.
 */
export function getDarkThemeExtendedColorComponents(name: ExtendedPaletteColorName): ColorComponents {
  return getDarkThemeExtendedColor(name).components;
}

/**
 * Returns the color definition for the specified color from the neutral palette.
 */
export function getNeutralColor(name: NeutralPaletteColorName): ColorDefinition {
  return NeutralPaletteColors[name];
}

/**
 * Returns an object with the RGBA values for the specified color from the neutral palette.
 */
export function getNeutralColorComponents(name: NeutralPaletteColorName): ColorComponents {
  return getNeutralColor(name).components;
}

// This implements the alpha compositing algorithm from this article:
// https://en.wikipedia.org/wiki/Alpha_compositing#Straight_versus_premultiplied
function overlayColor(
  r_base: number,
  g_base: number,
  b_base: number,
  a_base: number,
  r_overlay: number,
  g_overlay: number,
  b_overlay: number,
  a_overlay: number,
): [number, number, number, number] {
  const C_overlay = [r_overlay, g_overlay, b_overlay].map((v) => v / 255);
  const C_2 = [r_base, g_base, b_base].map((v) => v / 255);

  const [r, g, b] = C_overlay.map(
    (c_overlay, index) =>
      (c_overlay * a_overlay + C_2[index] * a_base * (1 - a_overlay)) / (a_overlay + a_base * (1 - a_overlay)),
  );
  const a = a_overlay + a_base * (1 - a_overlay);

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255), a];
}

export enum DataVisualizationPaletteColorName {
  Categorical1 = 'Categorical1',
  Categorical2 = 'Categorical2',
  Categorical3 = 'Categorical3',
  Categorical4 = 'Categorical4',
  Categorical5 = 'Categorical5',
  Categorical6 = 'Categorical6',
  Categorical7 = 'Categorical7',
  Categorical8 = 'Categorical8',
  Categorical9 = 'Categorical9',
  Categorical10 = 'Categorical10',
  Categorical11 = 'Categorical11',
  Categorical12 = 'Categorical12',
  Categorical13 = 'Categorical13',
  Categorical14 = 'Categorical14',
  Categorical15 = 'Categorical15',
  Categorical16 = 'Categorical16',
  Categorical17 = 'Categorical17',
  Categorical18 = 'Categorical18',
  Categorical19 = 'Categorical19',
  Categorical20 = 'Categorical20',
}

/* eslint-disable sort-keys-fix/sort-keys-fix */
const LightThemeDataVisualizationPaletteComponents = {
  [DataVisualizationPaletteColorName.Categorical1]: {
    r: 0,
    g: 135,
    b: 245,
  },
  [DataVisualizationPaletteColorName.Categorical2]: {
    r: 210,
    g: 113,
    b: 106,
  },
  [DataVisualizationPaletteColorName.Categorical3]: {
    r: 86,
    g: 159,
    b: 179,
  },
  [DataVisualizationPaletteColorName.Categorical4]: {
    r: 208,
    g: 132,
    b: 1,
  },
  [DataVisualizationPaletteColorName.Categorical5]: {
    r: 91,
    g: 58,
    b: 182,
  },
  [DataVisualizationPaletteColorName.Categorical6]: {
    r: 0,
    g: 107,
    b: 57,
  },
  [DataVisualizationPaletteColorName.Categorical7]: {
    r: 238,
    g: 93,
    b: 195,
  },
  [DataVisualizationPaletteColorName.Categorical8]: {
    r: 0,
    g: 200,
    b: 5,
  },
  [DataVisualizationPaletteColorName.Categorical9]: {
    r: 255,
    g: 80,
    b: 0,
  },
  [DataVisualizationPaletteColorName.Categorical10]: {
    r: 154,
    g: 147,
    b: 147,
  },
  [DataVisualizationPaletteColorName.Categorical11]: {
    r: 73,
    g: 57,
    b: 151,
  },
  [DataVisualizationPaletteColorName.Categorical12]: {
    r: 218,
    g: 122,
    b: 56,
  },
  [DataVisualizationPaletteColorName.Categorical13]: {
    r: 99,
    g: 102,
    b: 187,
  },
  [DataVisualizationPaletteColorName.Categorical14]: {
    r: 135,
    g: 158,
    b: 51,
  },
  [DataVisualizationPaletteColorName.Categorical15]: {
    r: 214,
    g: 51,
    b: 50,
  },
  [DataVisualizationPaletteColorName.Categorical16]: {
    r: 175,
    g: 78,
    b: 158,
  },
  [DataVisualizationPaletteColorName.Categorical17]: {
    r: 155,
    g: 41,
    b: 43,
  },
  [DataVisualizationPaletteColorName.Categorical18]: {
    r: 169,
    g: 145,
    b: 61,
  },
  [DataVisualizationPaletteColorName.Categorical19]: {
    r: 194,
    g: 55,
    b: 96,
  },
  [DataVisualizationPaletteColorName.Categorical20]: {
    r: 94,
    g: 161,
    b: 115,
  },
} as const;

/* eslint-disable sort-keys-fix/sort-keys-fix */
const DarkThemeDataVisualizationPaletteComponents = {
  [DataVisualizationPaletteColorName.Categorical1]: {
    r: 97,
    g: 184,
    b: 255,
  },
  [DataVisualizationPaletteColorName.Categorical2]: {
    r: 240,
    g: 170,
    b: 165,
  },
  [DataVisualizationPaletteColorName.Categorical3]: {
    r: 130,
    g: 200,
    b: 210,
  },
  [DataVisualizationPaletteColorName.Categorical4]: {
    r: 228,
    g: 145,
    b: 1,
  },
  [DataVisualizationPaletteColorName.Categorical5]: {
    r: 159,
    g: 129,
    b: 243,
  },
  [DataVisualizationPaletteColorName.Categorical6]: {
    r: 44,
    g: 170,
    b: 111,
  },
  [DataVisualizationPaletteColorName.Categorical7]: {
    r: 190,
    g: 130,
    b: 190,
  },
  [DataVisualizationPaletteColorName.Categorical8]: {
    r: 0,
    g: 200,
    b: 5,
  },
  [DataVisualizationPaletteColorName.Categorical9]: {
    r: 255,
    g: 80,
    b: 0,
  },
  [DataVisualizationPaletteColorName.Categorical10]: {
    r: 154,
    g: 147,
    b: 147,
  },
  [DataVisualizationPaletteColorName.Categorical11]: {
    r: 121,
    g: 104,
    b: 207,
  },
  [DataVisualizationPaletteColorName.Categorical12]: {
    r: 233,
    g: 149,
    b: 92,
  },
  [DataVisualizationPaletteColorName.Categorical13]: {
    r: 173,
    g: 175,
    b: 224,
  },
  [DataVisualizationPaletteColorName.Categorical14]: {
    r: 173,
    g: 201,
    b: 69,
  },
  [DataVisualizationPaletteColorName.Categorical15]: {
    r: 233,
    g: 109,
    b: 109,
  },
  [DataVisualizationPaletteColorName.Categorical16]: {
    r: 185,
    g: 70,
    b: 165,
  },
  [DataVisualizationPaletteColorName.Categorical17]: {
    r: 215,
    g: 66,
    b: 68,
  },
  [DataVisualizationPaletteColorName.Categorical18]: {
    r: 208,
    g: 179,
    b: 75,
  },
  [DataVisualizationPaletteColorName.Categorical19]: {
    r: 223,
    g: 114,
    b: 146,
  },
  [DataVisualizationPaletteColorName.Categorical20]: {
    r: 130,
    g: 192,
    b: 150,
  },
} as const;

/**
 * Returns a boolean indicating whether or not the specified string is the name of a color in
 * the data visualization palette. Also performs type narrowing.
 */
export function getIsDataVisualizationPaletteColorName(name: string): name is DataVisualizationPaletteColorName {
  return name in LightThemeDataVisualizationPaletteComponents || name in DarkThemeDataVisualizationPaletteComponents;
}

const LightThemeDataVisualizationPaletteColors = typeSafeObjectEntries(
  LightThemeDataVisualizationPaletteComponents,
).reduce(
  (obj, [name, components]) => {
    const { b, g, r } = components;

    return {
      ...obj,
      [name]: {
        base: `rgb(${r}, ${g}, ${b})`,
        components,
        hover: `rgba(${overlayColor(r, g, b, 1, 0, 0, 0, 0.1).join(', ')})`,
        light: `rgba(${r}, ${g}, ${b}, 0.3)`,
        lightest: `rgba(${r}, ${g}, ${b}, 0.1)`,
        pressed: `rgba(${overlayColor(r, g, b, 1, 0, 0, 0, 0.2).join(', ')})`,
      },
    };
  },
  {} as { [K in DataVisualizationPaletteColorName]: ColorDefinition },
);

const DarkThemeDataVisualizationPaletteColors = typeSafeObjectEntries(
  DarkThemeDataVisualizationPaletteComponents,
).reduce(
  (obj, [name, components]) => {
    const { b, g, r } = components;

    return {
      ...obj,
      [name]: {
        base: `rgb(${r}, ${g}, ${b})`,
        components,
        hover: `rgba(${overlayColor(r, g, b, 1, 255, 255, 255, 0.15).join(', ')})`,
        light: `rgba(${r}, ${g}, ${b}, 0.4)`,
        lightest: `rgba(${r}, ${g}, ${b}, 0.2)`,
        pressed: `rgba(${overlayColor(r, g, b, 1, 255, 255, 255, 0.25).join(', ')})`,
      },
    };
  },
  {} as { [K in DataVisualizationPaletteColorName]: ColorDefinition },
);

/**
 * Returns the color definition for the specified color from the DataVisualization palette in a
 * light theme.
 */
export function getLightThemeDataVisualizationColor(name: DataVisualizationPaletteColorName): ColorDefinition {
  return LightThemeDataVisualizationPaletteColors[name];
}

/**
 * Returns the color definition for the specified color from the DataVisualization palette in a
 * dark theme.
 */
export function getDarkThemeDataVisualizationColor(name: DataVisualizationPaletteColorName): ColorDefinition {
  return DarkThemeDataVisualizationPaletteColors[name];
}
